import { LOCALE } from '@/config';

import { type DictKey, type DictShape, en } from './en';
import { ru } from './ru';
import { uk } from './uk';

export type LocaleEntry = {
  code: string;
  label: string;
  dict: DictShape;
};

// Registry: adding a 4th locale is one entry here — no other code changes required.
export const locales: LocaleEntry[] = [
  { code: 'en', label: 'English', dict: { ...en } },
  { code: 'uk', label: 'Українська', dict: uk },
  { code: 'ru', label: 'Русский', dict: ru },
];

export { type DictKey, type DictShape, en };

/**
 * Determine the active UI locale. The route is the single source of truth: the
 * `/{lang}/` prefix in the current URL wins, so the UI language always matches the
 * page you are on (no reliance on persisted UI storage, which can drift from the
 * route). Falls back to navigator.language, then the default, only when the URL has
 * no language prefix (e.g. a bare `/`, which itself resolves to the default).
 */
export function detectLocale(): string {
  const codes = locales.map((l) => l.code);

  const fromRoute = window.location.pathname.match(/^\/([a-z]{2})(?:\/|$)/)?.[1];
  if (fromRoute && codes.includes(fromRoute)) return fromRoute;

  const nav = (navigator.language ?? '').split('-')[0];
  if (nav && codes.includes(nav)) return nav;

  return LOCALE.defaultCode;
}

/**
 * Resolve a key against a (potentially partial) dict, falling back to `en`.
 * `vars` entries are interpolated as `{{name}}` placeholders in the result.
 */
export function resolveKey(
  dict: Partial<DictShape>,
  key: DictKey,
  vars?: Record<string, string | number>,
): string {
  const raw = (dict[key] ?? (en[key as keyof typeof en] as string) ?? key) as string;
  if (!vars) return raw;
  return Object.entries(vars).reduce((acc, [k, v]) => acc.replace(`{{${k}}}`, String(v)), raw);
}
